/**
 * File: useAuthQueries.ts
 * Path: apps/admin/src/modules/auth/hooks/useAuthQueries.ts
 * Description: Read-only data fetching for authentication and user profiles.
 */

import { useQuery } from "@tanstack/react-query";
import { FirestoreService, FIRESTORE_COLLECTIONS } from "@idk/config";
import { AuthService } from "@/modules/auth/authService";
import { UserProfile } from "@idk/types";

export const useAuthQueries = () => {
  const collectionKey = FIRESTORE_COLLECTIONS.users;

  return {
    /**
     * Retrieves the current Firebase Auth user state.
     * Uses waitForAuth to ensure the session is resolved before returning data.
     */
    useCurrentUser: () =>
      useQuery({
        queryKey: ["auth", "user"],
        queryFn: () => AuthService.waitForAuth(),
        staleTime: Infinity,
      }),

    /**
     * Retrieves the extended Firestore profile document for a specific user UID.
     */
    useProfile: (uid?: string) =>
      useQuery({
        queryKey: [collectionKey, "profile", uid],
        queryFn: () =>
          FirestoreService.getById<UserProfile>(collectionKey, uid!),
        enabled: !!uid,
      }),
  };
};
